import type { CareerMovesSummary, CareerMoveBucket } from "@career-ops-amplify/shared";
import { cn } from "@/cn";

export type SummaryStripKey = "moves" | "replies" | "risk" | "goals";

type Props = {
  summary: CareerMovesSummary;
  activeFilter?: SummaryStripKey | null;
  onFilter?: (
    filter: SummaryStripKey | null,
    bucket?: CareerMoveBucket | "ALL_ACTIVE",
  ) => void;
  onNavigate?: (key: SummaryStripKey) => void;
};

export function CareerSummaryStrip({
  summary,
  activeFilter,
  onFilter,
  onNavigate,
}: Props) {
  const items = [
    {
      key: "moves" as const,
      label: "In queue",
      value: String(summary.movesToday),
      bucket: "ALL_ACTIVE" as const,
      navigate: false,
    },
    {
      key: "replies" as const,
      label: "Replies / follow-ups",
      value: String(summary.repliesNeeded),
      bucket: "REPLY_NEEDED" as const,
      navigate: false,
    },
    {
      key: "risk" as const,
      label: "At risk",
      value: String(summary.applicationsAtRisk),
      bucket: "AT_RISK" as const,
      navigate: false,
    },
    {
      key: "goals" as const,
      label: "Active goals",
      value: String(summary.activeGoals),
      bucket: "NURTURE" as const,
      navigate: true,
    },
  ];

  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
      {items.map((item) => {
        const active = activeFilter === item.key;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => {
              if (item.navigate && onNavigate) {
                onNavigate(item.key);
                return;
              }
              onFilter?.(
                active ? null : item.key,
                active ? undefined : item.bucket,
              );
            }}
            className={cn(
              "rounded-md border px-3 py-2.5 text-left transition",
              active
                ? "border-brand/40 bg-brand/5"
                : "border-border/80 bg-card/40 hover:bg-muted/20",
            )}
          >
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {item.label}
            </p>
            <p className="mt-1 text-xl font-semibold text-foreground">
              {item.value}
            </p>
          </button>
        );
      })}
    </div>
  );
}
